import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Inbox as InboxIcon, Paperclip, X as XIcon, Send, RefreshCw, ChevronDown,
    Download, ExternalLink, UserPlus, AlertTriangle, ArrowDownLeft,
} from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type {
    Communication, CommunicationInboxState, InboxCounts, Patient, PatientListItem,
} from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import { formatDateTime } from '../../../i18n/locale';
import PageHeader from '../ui/PageHeader';
import StatusPill from '../ui/StatusPill';
import type { StatusTone } from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import ComposeMailDialog from './ComposeMailDialog';
import PatientPicker from '../billing/PatientPicker';

// Posteingang (Mini-CRM Phase 2a, docs/CRM-PLAN.md): eingehende Patienten-Mails
// aus dem IMAP-Ingest lesen, unzugeordnete Mails einem Patienten zuordnen und
// direkt aus dem System antworten. Route /admin/inbox, Capability 'communications'.

const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

// Seitengröße der Inbox-Liste (API-Default/-Limit: 50).
const PAGE_LIMIT = 50;

type InboxFilter = 'new' | 'unmatched' | 'answered' | 'all';

const FILTER_CHIPS: Array<{ key: InboxFilter; labelKey: string; countKey: keyof InboxCounts }> = [
    { key: 'new', labelKey: 'crm.inbox.filterNew', countKey: 'new' },
    { key: 'unmatched', labelKey: 'crm.inbox.filterUnmatched', countKey: 'unmatched' },
    { key: 'answered', labelKey: 'crm.inbox.filterAnswered', countKey: 'answered' },
    { key: 'all', labelKey: 'crm.inbox.filterAll', countKey: 'total' },
];

const STATE_META: Record<CommunicationInboxState, { tone: StatusTone; labelKey: string }> = {
    new: { tone: 'info', labelKey: 'crm.state.new' },
    read: { tone: 'neutral', labelKey: 'crm.state.read' },
    answered: { tone: 'success', labelKey: 'crm.state.answered' },
    unmatched: { tone: 'warning', labelKey: 'crm.state.unmatched' },
};

const statePill = (state: CommunicationInboxState | null, t: (k: string) => string) => {
    if (!state) return null;
    const meta = STATE_META[state];
    if (!meta) return null;
    return <StatusPill tone={meta.tone} dot>{t(meta.labelKey)}</StatusPill>;
};

// Dateigröße menschenlesbar (Anhang-Buttons).
const formatSize = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// "Re: "-Präfix nur ergänzen, wenn noch keines da ist.
const replySubject = (subject: string): string =>
    /^re:/i.test(subject.trim()) ? subject : `Re: ${subject}`;

const InboxPage: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToast();

    const [filter, setFilter] = useState<InboxFilter>('new');
    const [items, setItems] = useState<Communication[]>([]);
    const [counts, setCounts] = useState<InboxCounts | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selected, setSelected] = useState<Communication | null>(null);
    const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

    // Antworten: ComposeMailDialog braucht die Patientenakte (Sprache, Name) —
    // wird beim Klick auf „Antworten" einmalig nachgeladen.
    const [replyTo, setReplyTo] = useState<{ comm: Communication; patient: Patient } | null>(null);
    const [replyLoading, setReplyLoading] = useState(false);

    // Zuordnen-Dialog (nur unmatched).
    const [assignFor, setAssignFor] = useState<Communication | null>(null);

    const load = useCallback(async (state: InboxFilter) => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiService.listInbox({ state, limit: PAGE_LIMIT });
            const batch = res.data.data?.items || [];
            setItems(batch);
            setCounts(res.data.data?.counts || null);
            setHasMore(batch.length >= PAGE_LIMIT);
        } catch {
            setError(t('crm.inbox.errorLoad'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load(filter);
    }, [filter, load]);

    const reload = () => load(filter);

    // before_id-Pagination (Muster CommunicationTimeline.loadMore).
    const loadMore = async () => {
        const last = items[items.length - 1];
        if (!last || loadingMore) return;
        try {
            setLoadingMore(true);
            const res = await apiService.listInbox({ state: filter, limit: PAGE_LIMIT, before_id: last.id });
            const batch = res.data.data?.items || [];
            setItems(prev => [...prev, ...batch]);
            if (res.data.data?.counts) setCounts(res.data.data.counts);
            setHasMore(batch.length >= PAGE_LIMIT);
        } catch {
            addToast(t('crm.inbox.errorLoad'), 'error');
        } finally {
            setLoadingMore(false);
        }
    };

    // Einen Eintrag lokal ersetzen (optimistische Updates, Zuordnung).
    const patchItem = (id: number, patch: Partial<Communication>) => {
        setItems(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
        setSelected(prev => (prev && prev.id === id ? { ...prev, ...patch } : prev));
    };

    // Öffnen einer neuen Mail markiert sie optimistisch als gelesen
    // (Liste + Zähler sofort, PATCH im Hintergrund; bei Fehler zurückrollen).
    const openItem = (c: Communication) => {
        setSelected(c);
        if (c.inbox_state !== 'new') return;
        patchItem(c.id, { inbox_state: 'read' });
        setCounts(prev => (prev ? { ...prev, new: Math.max(0, prev.new - 1), read: prev.read + 1 } : prev));
        apiService.setCommunicationState(c.id, 'read').catch(() => {
            patchItem(c.id, { inbox_state: 'new' });
            setCounts(prev => (prev ? { ...prev, new: prev.new + 1, read: Math.max(0, prev.read - 1) } : prev));
            addToast(t('crm.inbox.markReadFailed'), 'error');
        });
    };

    // Anhang mit Auth-Header laden und als Datei-Download anstoßen.
    const downloadAttachment = async (c: Communication, index: number, filename: string) => {
        try {
            setDownloadingIndex(index);
            const blob = await apiService.fetchCommunicationAttachment(c.id, index);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `attachment-${index}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            addToast(t('crm.inbox.downloadFailed'), 'error');
        } finally {
            setDownloadingIndex(null);
        }
    };

    // „Antworten": Patientenakte nachladen und ComposeMailDialog vorbefüllt öffnen.
    const startReply = async (c: Communication) => {
        if (c.patient_id == null || replyLoading) return;
        try {
            setReplyLoading(true);
            const res = await apiService.getPatient(c.patient_id);
            const patient = res.data.data;
            if (!patient) throw new Error('missing patient');
            setReplyTo({ comm: c, patient });
        } catch {
            addToast(t('crm.inbox.replyLoadFailed'), 'error');
        } finally {
            setReplyLoading(false);
        }
    };

    const senderLabel = (c: Communication) =>
        c.from_name || c.from_address || t('crm.inbox.unknownSender');

    return (
        <div>
            <PageHeader
                eyebrow={t('crm.inbox.eyebrow')}
                title={t('crm.inbox.title')}
                description={t('crm.inbox.description')}
                actions={
                    <button
                        type="button"
                        onClick={reload}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-surface-2 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
                        {t('crm.inbox.reload')}
                    </button>
                }
            />

            {/* Filter-Pills mit Zählern (Muster EventLogPage-Chips) */}
            <div className="mb-4 flex items-center gap-1 bg-brand-surface border border-brand-border rounded-md p-0.5 w-fit max-w-full overflow-x-auto">
                {FILTER_CHIPS.map(chip => (
                    <button
                        key={chip.key}
                        type="button"
                        onClick={() => setFilter(chip.key)}
                        className={`px-3 py-1 text-[12px] font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                            filter === chip.key
                                ? 'bg-brand-primary text-white shadow-sm'
                                : 'text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2'
                        }`}
                    >
                        {t(chip.labelKey)}
                        {counts && (
                            <span className={`ml-1.5 tabular-nums ${filter === chip.key ? 'text-white/80' : 'text-brand-text-muted'}`}>
                                {counts[chip.countKey]}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Liste */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
                {loading && items.length === 0 ? (
                    <div className="p-12 text-center text-sm text-brand-text-muted">{t('crm.inbox.loading')}</div>
                ) : error ? (
                    <div className="p-12 text-center text-sm text-rose-600">{error}</div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={InboxIcon}
                        title={t('crm.inbox.emptyTitle')}
                        description={t('crm.inbox.emptyDescription')}
                    />
                ) : (
                    <ul className="divide-y divide-brand-border">
                        {items.map(c => (
                            <li key={c.id}>
                                {/* Bewusst kein <button>: die Zeile enthält einen Link („Zur Akte")
                                    und verschachtelte Interaktivelemente wären invalides HTML. */}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openItem(c)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem(c); } }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-brand-surface-2 transition-colors cursor-pointer"
                                >
                                    <ArrowDownLeft className="h-4 w-4 shrink-0 text-slate-500" strokeWidth={1.75} aria-hidden />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <span className={`text-[13px] truncate ${c.inbox_state === 'new' ? 'font-semibold text-brand-text' : 'font-medium text-brand-text'}`}>
                                                {senderLabel(c)}
                                            </span>
                                            {(c.attachments?.length ?? 0) > 0 && (
                                                <Paperclip className="h-3.5 w-3.5 shrink-0 text-brand-text-muted" strokeWidth={1.75} aria-label={t('crm.inbox.attachments')} />
                                            )}
                                        </div>
                                        <div className="text-xs text-brand-text-muted truncate">
                                            {c.subject || t('crm.timeline.noSubject')}
                                        </div>
                                    </div>
                                    <span className="hidden sm:block text-xs text-brand-text-muted tabular-nums shrink-0">
                                        {formatDateTime(c.sent_at || c.created_at)}
                                    </span>
                                    {c.patient_id != null && (
                                        <Link
                                            to={`/admin/patients/${c.patient_id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors shrink-0"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                                            {t('crm.inbox.toPatient')}
                                        </Link>
                                    )}
                                    <span className="shrink-0">{statePill(c.inbox_state, t)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {!loading && !error && items.length > 0 && hasMore && (
                    <div className="border-t border-brand-border">
                        <button
                            type="button"
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="w-full flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-medium text-brand-primary hover:text-brand-primary-hover hover:bg-brand-surface-2 transition-colors cursor-pointer disabled:opacity-50"
                        >
                            <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                            {loadingMore ? t('crm.inbox.loading') : t('crm.inbox.loadMore')}
                        </button>
                    </div>
                )}
            </div>

            {/* Detail-Modal (Overlay-Muster wie CommunicationTimeline) */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4 bg-black/50 overflow-y-auto" onClick={() => setSelected(null)}>
                    <div className="w-full max-w-2xl rounded-xl bg-brand-surface border border-brand-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                            <div className="flex items-center gap-2 min-w-0">
                                <h3 className="text-sm font-semibold text-brand-text truncate">{t('crm.timeline.inboundLabel')}</h3>
                                {statePill(selected.inbox_state, t)}
                            </div>
                            <button onClick={() => setSelected(null)} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('common.close')}>
                                <XIcon className="h-4 w-4" strokeWidth={1.75} />
                            </button>
                        </div>

                        <div className="p-5 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <span className={labelClass}>{t('crm.detail.from')}</span>
                                    <p className="text-sm text-brand-text break-all">
                                        {selected.from_name ? <>{selected.from_name} · </> : null}
                                        {selected.from_address || '—'}
                                    </p>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('crm.detail.to')}</span>
                                    <p className="text-sm text-brand-text break-all">{selected.to || '—'}</p>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('crm.detail.subject')}</span>
                                    <p className="text-sm text-brand-text">{selected.subject || t('crm.timeline.noSubject')}</p>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('crm.detail.time')}</span>
                                    <p className="text-sm text-brand-text tabular-nums">{formatDateTime(selected.sent_at || selected.created_at)}</p>
                                </div>
                            </div>

                            {selected.body_text ? (
                                <pre className="whitespace-pre-wrap max-h-72 overflow-y-auto px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md text-[12.5px] font-mono leading-relaxed text-brand-text">
                                    {selected.body_text}
                                </pre>
                            ) : (
                                <p className="px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md text-xs italic text-brand-text-muted">
                                    {t('crm.detail.bodyNotArchived')}
                                </p>
                            )}

                            {(selected.attachments?.length ?? 0) > 0 && (
                                <div>
                                    <span className={labelClass}>{t('crm.inbox.attachments')}</span>
                                    <div className="flex flex-wrap gap-2">
                                        {selected.attachments.map((att, idx) => (
                                            <button
                                                key={`${idx}-${att.filename}`}
                                                type="button"
                                                onClick={() => downloadAttachment(selected, idx, att.filename)}
                                                disabled={downloadingIndex !== null}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-brand-text bg-brand-surface-2/60 border border-brand-border rounded-md hover:bg-brand-surface-2 transition-colors cursor-pointer disabled:opacity-50 max-w-full"
                                            >
                                                <Download className={`h-3.5 w-3.5 shrink-0 ${downloadingIndex === idx ? 'animate-pulse' : ''}`} strokeWidth={1.75} />
                                                <span className="truncate">{att.filename}</span>
                                                {att.size > 0 && <span className="text-brand-text-muted shrink-0">({formatSize(att.size)})</span>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(selected.attachments_skipped ?? 0) > 0 && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md text-xs text-amber-800 dark:text-amber-300">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                                    <span>{t('crm.inbox.attachmentsSkipped', { count: selected.attachments_skipped })}</span>
                                </div>
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-brand-border flex items-center justify-between gap-2">
                            <div>
                                {selected.patient_id != null && (
                                    <Link
                                        to={`/admin/patients/${selected.patient_id}`}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        {t('crm.inbox.toPatient')}
                                    </Link>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {selected.inbox_state === 'unmatched' && (
                                    <button
                                        type="button"
                                        onClick={() => setAssignFor(selected)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                                    >
                                        <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
                                        {t('crm.inbox.assign')}
                                    </button>
                                )}
                                {selected.patient_id != null && (
                                    <button
                                        type="button"
                                        onClick={() => startReply(selected)}
                                        disabled={replyLoading}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50"
                                    >
                                        <Send className="h-3.5 w-3.5" strokeWidth={2} />
                                        {t('crm.inbox.reply')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setSelected(null)}
                                    className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer"
                                >
                                    {t('common.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Antworten: vorbefüllter ComposeMailDialog (Thread über reply_to_communication_id) */}
            {replyTo && (
                <ComposeMailDialog
                    patient={replyTo.patient}
                    replyToCommunicationId={replyTo.comm.id}
                    initialTo={replyTo.comm.from_address || replyTo.patient.email || ''}
                    initialSubject={replySubject(replyTo.comm.subject || '')}
                    onClose={() => setReplyTo(null)}
                    onSent={() => { setSelected(null); reload(); }}
                />
            )}

            {/* Patient zuordnen (nur unmatched) */}
            {assignFor && (
                <AssignPatientDialog
                    communication={assignFor}
                    onClose={() => setAssignFor(null)}
                    onAssigned={() => { setAssignFor(null); setSelected(null); reload(); }}
                />
            )}
        </div>
    );
};

// ====================================================================
// AssignPatientDialog — unzugeordnete Inbound-Mail einem Patienten zuordnen.
// Patientensuche über den bestehenden billing/PatientPicker; optional wird die
// Absenderadresse als Zweit-E-Mail am Patienten gespeichert (Default: an).
// ====================================================================

interface AssignPatientDialogProps {
    communication: Communication;
    onClose: () => void;
    onAssigned: () => void;
}

const AssignPatientDialog: React.FC<AssignPatientDialogProps> = ({ communication, onClose, onAssigned }) => {
    const { t } = useTranslation();
    const { addToast } = useToast();

    const [picked, setPicked] = useState<PatientListItem | null>(null);
    const [saveAlternate, setSaveAlternate] = useState(true);
    const [assigning, setAssigning] = useState(false);

    const assign = async () => {
        if (!picked || assigning) return;
        try {
            setAssigning(true);
            const res = await apiService.assignCommunicationPatient(communication.id, picked.id, saveAlternate);
            const alternateSaved = res.data.data?.alternate_saved === true;
            addToast(alternateSaved ? t('crm.inbox.assignOkAlternate') : t('crm.inbox.assignOk'), 'success');
            onAssigned();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('crm.inbox.assignFailed'), 'error');
        } finally {
            setAssigning(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4 bg-black/50 overflow-y-auto" onClick={onClose}>
            <div className="w-full max-w-md rounded-xl bg-brand-surface border border-brand-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                    <h3 className="text-sm font-semibold text-brand-text flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
                        {t('crm.inbox.assignTitle')}
                    </h3>
                    <button onClick={onClose} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('common.close')}>
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    <p className="text-xs text-brand-text-muted">{t('crm.inbox.assignHint')}</p>
                    <p className="text-sm text-brand-text break-all">
                        {communication.from_name ? <>{communication.from_name} · </> : null}
                        <span className="font-medium">{communication.from_address || '—'}</span>
                    </p>

                    <PatientPicker value={picked} onPick={setPicked} disabled={assigning} />

                    <label className="flex items-center gap-2 text-[13px] text-brand-text cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={saveAlternate}
                            onChange={(e) => setSaveAlternate(e.target.checked)}
                            disabled={assigning}
                            className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                        />
                        {t('crm.inbox.assignSaveAlternate')}
                    </label>
                </div>

                <div className="px-5 py-3 border-t border-brand-border flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={assigning}
                        className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer disabled:opacity-50"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={assign}
                        disabled={!picked || assigning}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
                        {assigning ? t('crm.inbox.assigning') : t('crm.inbox.assignConfirm')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InboxPage;
